import { Injectable } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AuthenticationResult, HandleRedirectPromiseOptions } from '@azure/msal-browser';
import { Observable, from, of } from 'rxjs';
import { catchError, finalize, shareReplay, switchMap } from 'rxjs/operators';
import { ensureMsalInitialized, isBenignMsalRedirectError } from './msal.setup';

/**
 * Ensures MSAL redirect handling runs exactly once per page load with correct URL parsing.
 * MsalService.handleRedirectObservable() can pass a malformed hash for query-string OAuth
 * responses (e.g. `/?state=...&code=...`), which prevents token acquisition.
 */
@Injectable({ providedIn: 'root' })
export class MsalRedirectService {
  private redirectOnce$?: Observable<AuthenticationResult | null>;

  constructor(
    private readonly msalService: MsalService,
    private readonly broadcastService: MsalBroadcastService,
  ) {}

  handleRedirectOnce(): Observable<AuthenticationResult | null> {
    if (!this.redirectOnce$) {
      this.redirectOnce$ = from(ensureMsalInitialized()).pipe(
        switchMap(() => from(this.msalService.instance.handleRedirectPromise(this.buildRedirectOptions()))),
        catchError((err: unknown) => {
          if (!isBenignMsalRedirectError(err)) {
            console.warn('MSAL redirect handling failed:', err);
          }
          return of(null);
        }),
        finalize(() => {
          this.broadcastService.resetInProgressEvent();
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    }
    return this.redirectOnce$;
  }

  private buildRedirectOptions(): HandleRedirectPromiseOptions {
    const options: HandleRedirectPromiseOptions = { navigateToLoginRequestUrl: true };
    const fragment = window.location.hash;
    // Only pass hash when OAuth params live in the URL fragment; query-string responses
    // must be read from window.location.search by MSAL (do not use MsalService.redirectHash).
    if (fragment && this.fragmentHasOAuthResponse(fragment)) {
      return { ...options, hash: fragment };
    }
    return options;
  }

  private fragmentHasOAuthResponse(fragment: string): boolean {
    return /(?:^|[#&?])(code|state|error|id_token|access_token)=/i.test(fragment);
  }
}
